// ROI Reporting — the "simple ROI model".
//
// Deliberately does NOT try to price physiological deltas (HRV, sleep, recovery).
// Instead it models the BUSINESS outcomes an intervention influences — absence,
// presenteeism, productivity, injury, turnover, healthcare — from admin-entered
// workforce assumptions × published UK cost benchmarks, then applies a single
// attribution factor (how much of that headline the programme plausibly moves).
//
// Every default carries a cited source (see SOURCES) so the output is defensible
// in front of a finance stakeholder. Nothing here calls the backend — it is a
// pure, deterministic calculator so the numbers are auditable line-by-line.

export interface RoiInputs {
  /** Total employees in scope. */
  headcount: number;
  /** Average fully-loaded cost of one lost working day (salary + cover + admin). */
  costPerSickDay: number;
  /** Baseline sickness-absence days per employee per year. */
  absenceDaysPerEmployee: number;
  /** Productive days lost to presenteeism per employee per year. */
  presenteeismDaysPerEmployee: number;
  /** Output value of one productive working day per employee. */
  productivityValuePerDay: number;
  /** Baseline voluntary/ill-health leavers per year. */
  turnoverLeaversPerYear: number;
  /** Fully-loaded cost to replace one leaver (recruit + onboard + lost output). */
  costPerReplacement: number;
  /** Lost-time / MSK injuries per year. */
  injuriesPerYear: number;
  /** Average cost of one injury case. */
  costPerInjury: number;
  /** Occupational-health / healthcare referrals per year. */
  healthcareReferralsPerYear: number;
  /** Average cost of one healthcare referral / claim episode. */
  costPerReferral: number;
  /**
   * Share of the headline opportunity credibly attributable to the programme,
   * 0..1. The doc's "even at 10–20% influence" caveat. Applied uniformly.
   */
  attribution: number;
  /** Fractional reductions per category the programme targets, 0..1. */
  reductions: {
    absence: number;
    presenteeism: number;
    productivity: number;
    injury: number;
    turnover: number;
    healthcare: number;
  };
}

export type RoiCategoryKey =
  | "absence"
  | "presenteeism"
  | "productivity"
  | "injury"
  | "turnover"
  | "healthcare";

export interface RoiCategoryResult {
  key: RoiCategoryKey;
  label: string;
  /** The full annual cost pool this category represents. */
  headline: number;
  /** Gross saving from the targeted reduction, before attribution. */
  grossSaving: number;
  /** grossSaving × attribution — the figure we actually claim. */
  attributedSaving: number;
  /** Human-readable derivation, e.g. "500 days × £120 × 15%". */
  basis: string;
}

export interface RoiResult {
  categories: RoiCategoryResult[];
  totalHeadline: number;
  totalGross: number;
  totalAttributed: number;
  perEmployeeAttributed: number;
}

/**
 * Sensible, source-backed UK defaults for a mid-size workforce. Admins tune
 * these to their own organisation — the defaults exist so the page shows a
 * credible worked example on first load rather than zeros.
 */
export const DEFAULT_INPUTS: RoiInputs = {
  headcount: 100,
  costPerSickDay: 120, // gov.uk Keep Britain Working: ~£120 lost profit / sick day
  absenceDaysPerEmployee: 6, // ~150m days / UK workforce ≈ mid-single-digit / head
  presenteeismDaysPerEmployee: 6, // gov.uk: 4–9 productive days lost / employee / yr
  productivityValuePerDay: 250, // UK equivalent output £150–£400 / day
  turnoverLeaversPerYear: 10,
  costPerReplacement: 11000, // gov.uk: >£11k to replace an ill-health leaver
  injuriesPerYear: 5,
  costPerInjury: 8000, // lost-time / MSK mid-range
  healthcareReferralsPerYear: 40,
  costPerReferral: 300, // OH referral £150–£500
  attribution: 0.15, // conservative middle of the doc's 10–20%
  reductions: {
    absence: 0.1,
    presenteeism: 0.1,
    productivity: 0.02, // 2% productivity uplift
    injury: 0.15,
    turnover: 0.1,
    healthcare: 0.1,
  },
};

const money = (n: number): string =>
  `£${Math.round(n).toLocaleString("en-GB")}`;
const pct = (n: number): string => `${Math.round(n * 100)}%`;

/** Pure calculator. Given inputs, returns per-category and total savings. */
export const computeRoi = (input: RoiInputs): RoiResult => {
  const {
    headcount: hc,
    reductions: r,
    attribution: a,
  } = input;

  const rows: Omit<RoiCategoryResult, "attributedSaving">[] = [
    (() => {
      const headline =
        hc * input.absenceDaysPerEmployee * input.costPerSickDay;
      const grossSaving = headline * r.absence;
      return {
        key: "absence" as const,
        label: "Absenteeism",
        headline,
        grossSaving,
        basis: `${hc} staff × ${input.absenceDaysPerEmployee} days × ${money(
          input.costPerSickDay,
        )} × ${pct(r.absence)} reduction`,
      };
    })(),
    (() => {
      const headline =
        hc * input.presenteeismDaysPerEmployee * input.productivityValuePerDay;
      const grossSaving = headline * r.presenteeism;
      return {
        key: "presenteeism" as const,
        label: "Presenteeism",
        headline,
        grossSaving,
        basis: `${hc} staff × ${input.presenteeismDaysPerEmployee} lost days × ${money(
          input.productivityValuePerDay,
        )} × ${pct(r.presenteeism)} reduction`,
      };
    })(),
    (() => {
      // Productivity uplift is applied to a full working year (~220 days).
      const workingDays = 220;
      const headline = hc * workingDays * input.productivityValuePerDay;
      const grossSaving = headline * r.productivity;
      return {
        key: "productivity" as const,
        label: "Productivity gain",
        headline,
        grossSaving,
        basis: `${hc} staff × ${workingDays} days × ${money(
          input.productivityValuePerDay,
        )} × ${pct(r.productivity)} uplift`,
      };
    })(),
    (() => {
      const headline = input.injuriesPerYear * input.costPerInjury;
      const grossSaving = headline * r.injury;
      return {
        key: "injury" as const,
        label: "Injury & safety",
        headline,
        grossSaving,
        basis: `${input.injuriesPerYear} injuries × ${money(
          input.costPerInjury,
        )} × ${pct(r.injury)} reduction`,
      };
    })(),
    (() => {
      const headline =
        input.turnoverLeaversPerYear * input.costPerReplacement;
      const grossSaving = headline * r.turnover;
      return {
        key: "turnover" as const,
        label: "Turnover & retention",
        headline,
        grossSaving,
        basis: `${input.turnoverLeaversPerYear} leavers × ${money(
          input.costPerReplacement,
        )} × ${pct(r.turnover)} reduction`,
      };
    })(),
    (() => {
      const headline =
        input.healthcareReferralsPerYear * input.costPerReferral;
      const grossSaving = headline * r.healthcare;
      return {
        key: "healthcare" as const,
        label: "Healthcare & insurance",
        headline,
        grossSaving,
        basis: `${input.healthcareReferralsPerYear} referrals × ${money(
          input.costPerReferral,
        )} × ${pct(r.healthcare)} reduction`,
      };
    })(),
  ];

  const categories: RoiCategoryResult[] = rows.map((row) => ({
    ...row,
    attributedSaving: row.grossSaving * a,
  }));

  const totalHeadline = categories.reduce((s, c) => s + c.headline, 0);
  const totalGross = categories.reduce((s, c) => s + c.grossSaving, 0);
  const totalAttributed = categories.reduce(
    (s, c) => s + c.attributedSaving,
    0,
  );

  return {
    categories,
    totalHeadline,
    totalGross,
    totalAttributed,
    perEmployeeAttributed: hc > 0 ? totalAttributed / hc : 0,
  };
};

export interface EvidenceRow {
  area: string;
  stat: string;
  why: string;
  source: string;
}

/** Cited evidence backing the model — surfaced on the Methodology tab. */
export const SOURCES: EvidenceRow[] = [
  {
    area: "Cost of sickness absence",
    stat: "Each sickness-absence day costs employers ~£120 in lost profit.",
    why: "Anchors the absence and cover cost per day.",
    source:
      "https://www.gov.uk/government/publications/keep-britain-working-review-final-report/keep-britain-working-final-report",
  },
  {
    area: "Cost of poor workplace health",
    stat: "Poor workplace health costs UK employers ~£85bn per year.",
    why: "Scale of the preventable opportunity.",
    source:
      "https://www.gov.uk/government/publications/keep-britain-working-review-final-report/keep-britain-working-final-report",
  },
  {
    area: "Employee replacement (ill-health)",
    stat: "Replacing an ill-health leaver costs >£11,000.",
    why: "Anchors the turnover replacement cost.",
    source:
      "https://www.gov.uk/government/publications/keep-britain-working-review-final-report/keep-britain-working-final-report",
  },
  {
    area: "Professional replacement cost",
    stat: "Replacing a professional costs ~£30,000 all-in.",
    why: "Upper anchor for skilled-role turnover.",
    source:
      "https://www.oxfordeconomics.com/wp-content/uploads/2023/05/cost-brain-drain-report.pdf",
  },
  {
    area: "Presenteeism volume",
    stat: "Working while unwell loses 4–9 productive days per employee per year.",
    why: "Anchors presenteeism days input.",
    source:
      "https://www.gov.uk/government/publications/keep-britain-working-review-final-report/keep-britain-working-final-report",
  },
  {
    area: "Cost of presenteeism",
    stat: "Presenteeism costs UK employers ~£21bn per year.",
    why: "Validates presenteeism as the largest hidden pool.",
    source:
      "https://www.gov.uk/government/publications/keep-britain-working-review-final-report/keep-britain-working-technical-note",
  },
  {
    area: "Mental health ROI",
    stat: "Every £1 in workplace mental health returns £4.70–£5.30.",
    why: "Benchmarks plausible programme ROI multiples.",
    source:
      "https://www.deloitte.com/content/dam/assets-zone2/uk/en/docs/industries/life-sciences-health-care/2025/the-shift-to-prevention-realising-the-socio-economic-potential-v2.pdf",
  },
  {
    area: "EAP ROI",
    stat: "Every £1 in Employee Assistance Programmes returns ~£8.",
    why: "Comparator for proactive support ROI.",
    source:
      "https://www.deloitte.com/content/dam/assets-zone2/uk/en/docs/industries/energy-resources-industrials/2023/deloitte-uk-mental-health-report-2022.pdf",
  },
  {
    area: "MSK intervention ROI",
    stat: "£11–£99 returned per £1 invested in MSK interventions.",
    why: "Supports the injury/MSK category for physical roles.",
    source:
      "https://www.gov.uk/government/news/new-return-on-investment-tool-on-musculoskeletal-conditions",
  },
  {
    area: "Occupational safety ROI",
    stat: "Every £1 in occupational safety returns ~£2.20.",
    why: "Conservative anchor for injury reduction value.",
    source:
      "https://www.issa.int/sites/default/files/documents/publications/2-Return-on-prevention_en-29437.pdf",
  },
  {
    area: "Sleep & productivity",
    stat: "Under-6h sleepers cost the UK economy ~£40bn per year.",
    why: "Links the sleep/fatigue focus to productivity losses.",
    source: "https://www.rand.org/pubs/research_reports/RR1791.html",
  },
  {
    area: "Working days lost",
    stat: "~150m working days lost annually to sickness absence in the UK.",
    why: "Normalises absence-days-per-head assumption.",
    source:
      "https://www.gov.uk/government/publications/keep-britain-working-review-final-report/keep-britain-working-final-report",
  },
  {
    area: "Economic inactivity",
    stat: "Work-related ill health costs the UK ~£212bn/yr (~7% of GDP).",
    why: "Macro context for the business case.",
    source:
      "https://www.gov.uk/government/publications/keep-britain-working-review-final-report/keep-britain-working-final-report",
  },
];

export { money as formatMoney, pct as formatPct };
